// One group (design system 06-modules §2; permission-model 05-administration §4): the detail
// surface holding both editors — what it confers and who is in it — plus what a member can
// actually do after combine and floors, the foot-gun warnings at the point of editing, the soft
// rider labelled as advisory, and a deletion that states its blast radius before it happens.
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { AdminGroupDetail, AdminUserRow, AdminUsersResponse, GroupAuditEntry, GroupAuditResponse, GroupGrants } from '../../shared/api-types.js';
import {
  ADMIN_SECTION_DESCRIPTIONS,
  ADMIN_SECTION_LABELS,
  ADMIN_SECTIONS,
  GLOBAL_CAPABILITIES,
  GLOBAL_CAPABILITY_LABELS,
  type AdminSection,
  type GlobalCapability,
} from '../../shared/enums.js';
import { failureKind, queryString, type ApiFailure, type ApiResult } from '../api.js';
import { PATHS } from '../paths.js';
import { AdminSectionStrip } from '../shell/admin.js';
import { useMeContext } from '../shell/me-context.js';
import {
  Banner,
  Button,
  Checkbox,
  Chip,
  ConfirmDialog,
  DataTable,
  Dialog,
  FailureView,
  Fieldset,
  FormErrorSummary,
  LinkButton,
  Page,
  PageHead,
  RelativeTime,
  Section,
  SelectField,
  Skeleton,
  StatusPill,
  TextField,
  TextareaField,
  formatCount,
  formatListWithRest,
  inlineFailureText,
  pluralise,
  useRequest,
  useToast,
  type DataTableColumn,
  type DataTableStatus,
  type RequestState,
} from '../ui/index.js';
import { adminApi } from './admin-api.js';
import {
  GROUP_WARNING_TITLES,
  SCOPE_MEMBER_LABEL,
  actDelegableSectionsOf,
  capabilityDescription,
  describeBlastRadius,
  grantsEqual,
  normaliseGrants,
  operationLabel,
  sectionReachLabel,
  setGlobalGrant,
  setScopeMember,
  setSectionGrant,
} from './admin-helpers.js';
import { UnsavedChangesGuard } from '../ui/unsaved-changes.js';
import { IdentityCell, OperationFailure } from './parts.js';
import './admin.css';

export default function AdminGroupDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { me, reload: reloadMe } = useMeContext();
  const navigate = useNavigate();
  const toast = useToast();
  const detail = useRequest<AdminGroupDetail>((signal) => adminApi.group(id, signal), [id]);
  const trail = useRequest<GroupAuditResponse>((signal) => adminApi.groupAudit(id, signal), [id]);
  const group = detail.data;

  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [attempted, setAttempted] = useState('change this group');

  const reportFailure = (result: ApiFailure, what: string): void => {
    setAttempted(what);
    setFailure(result);
    if (failureKind(result) === 'denied') {
      // Why: the server re-resolved our permission and refused; the shell must reflect it.
      reloadMe();
      detail.reload();
    }
  };

  /** Runs a mutation that answers with the group detail; the trail refreshes since every one writes to it. */
  const run = async (what: string, call: () => Promise<ApiResult<AdminGroupDetail>>, success: string, undo?: { label: string; action: () => void }): Promise<boolean> => {
    setBusy(true);
    setFailure(null);
    const result = await call();
    setBusy(false);
    if (!result.ok) {
      reportFailure(result, what);
      return false;
    }
    detail.setData(result.data);
    trail.reload();
    toast.success(success, undo ? { action: { label: undo.label, onClick: undo.action } } : undefined);
    return true;
  };

  const crumbs = [{ label: 'Groups', to: PATHS.adminGroups, role: 'module' as const }];
  // Why: the group's own create/update/delete rows live in the general audit log (targetType 'group'),
  // separate from the history below; the account page offers the same route in the same place.
  const auditLink = group ? `${PATHS.adminAudit}${queryString({ target: group.id, targetType: 'group' })}` : PATHS.adminAudit;

  if (detail.status === 'error' && detail.error && !group) {
    return (
      <Page measure="read-wide">
        <PageHead title="Group" crumbs={crumbs} strip={<AdminSectionStrip />} />
        <FailureView failure={detail.error} what="this group" onRetry={detail.reload} backTo={{ to: PATHS.adminGroups, label: 'Back to groups' }} minHeight="40dvh" />
      </Page>
    );
  }

  return (
    <Page measure="read-wide">
      <PageHead
        title={group?.name ?? 'Group'}
        subtitle={group?.description ?? undefined}
        crumbs={crumbs}
        strip={<AdminSectionStrip />}
        actions={group ? (
          <>
            {me.admin.audit.view ? <LinkButton to={auditLink} icon="list" variant="secondary">View audit trail</LinkButton> : null}
            <Button variant="secondary" icon="edit" onClick={() => setEditing(true)}>Edit details</Button>
            <Button variant="danger-outline" icon="trash" onClick={() => setDeleting(true)}>Delete group</Button>
          </>
        ) : undefined}
      />

      <OperationFailure failure={failure} attempted={attempted} onDismiss={() => setFailure(null)} />

      {group?.policyNote ? (
        <Banner tone="info" title="Advisory — enforces nothing" icon="info">
          <p>{group.policyNote}</p>
        </Banner>
      ) : null}

      <Section id="grants" title="What it confers" description="Unticking a box removes nothing a member holds through another group.">
        {group ? (
          <div className="stack">
            {group.warnings.map((warning) => (
              <Banner key={`${warning.code}:${warning.capability ?? ''}`} tone="warning" title={GROUP_WARNING_TITLES[warning.code]}>
                {warning.message}
              </Banner>
            ))}
            <GrantsEditor
              group={group}
              busy={busy}
              onSave={(grants) => run('save these grants', () => adminApi.setGrants(group.id, grants), `Saved what ${group.name} confers. Members see the change on their next request.`)}
            />
          </div>
        ) : <Skeleton width="60%" />}
      </Section>

      <Section id="effective" title="Effective access" description="What a member holding only this group can do, from the saved grants and this installation's configuration.">
        {group ? <EffectiveAccess group={group} /> : <Skeleton width="50%" />}
      </Section>

      <Section id="members" title={group ? `Members (${formatCount(group.memberCount)})` : 'Members'} description="Adding or removing takes effect on the person's next request.">
        {group ? (
          <MembersEditor
            group={group}
            busy={busy}
            onChange={(subjectIds, message, previous) => run(
              'change the members',
              () => adminApi.setMembers(group.id, subjectIds),
              message,
              { label: 'Undo', action: () => { void run('undo the membership change', () => adminApi.setMembers(group.id, previous), 'Membership restored.'); } },
            )}
          />
        ) : <Skeleton width="60%" />}
      </Section>

      <Section id="history" title="Change history" description="Newest first; kept indefinitely, separate from the audit log.">
        <GroupTrail trail={trail} />
      </Section>

      {group ? (
        <>
          <EditDetailsDialog
            group={group}
            open={editing}
            busy={busy}
            onClose={() => setEditing(false)}
            onSave={async (body) => {
              const ok = await run('save the details', () => adminApi.updateGroup(group.id, body), 'Saved the group details.');
              if (ok) setEditing(false);
              return ok;
            }}
          />
          <DeleteGroupDialog
            group={group}
            open={deleting}
            onCancel={() => setDeleting(false)}
            onDeleted={(name) => {
              toast.success(`Deleted ${name}.`);
              navigate(PATHS.adminGroups, { replace: true });
            }}
            onDenied={(result) => { setDeleting(false); reportFailure(result, 'delete this group'); }}
            onChanged={() => detail.reload()}
          />
        </>
      ) : null}
    </Page>
  );
}

// --- Grants editor ---------------------------------------------------------------------------------

/** Exported for the rendering tests (test/server/client-pages/admin-groups.test.ts); the page composes it. */
export function GrantsEditor({ group, busy, onSave }: { group: AdminGroupDetail; busy: boolean; onSave: (grants: GroupGrants) => Promise<boolean> }) {
  const saved = useMemo(() => normaliseGrants(group.confers), [group.confers]);
  const [draft, setDraft] = useState<GroupGrants>(saved);
  useEffect(() => { setDraft(saved); }, [saved]);
  const dirty = !grantsEqual(draft, saved);
  const floorFor = (name: GlobalCapability): boolean => group.effectiveAccess.globals.find((entry) => entry.name === name)?.floorMet ?? true;
  const sectionOf = (section: AdminSection) => draft.sections.find((entry) => entry.section === section) ?? { section, view: false, act: false };
  // Why: permission-model 05-administration §4 — a tick that opens nothing is not offered. The server says per
  // section whether any delegable route honours `act` (effectiveAccess.sections[].actDelegable, from
  // authz/route-map.ts); without that word there is no tick, so a section the server did not name fails closed.
  // Conformity finding G3; the owner's pick C4 of 2026-10-05.
  const actOffered = (section: AdminSection): boolean => group.effectiveAccess.sections.find((entry) => entry.section === section)?.actDelegable ?? false;

  return (
    <form
      className="grants-editor"
      onSubmit={(event) => {
        event.preventDefault();
        if (dirty && !busy) void onSave(draft);
      }}
    >
      <Fieldset legend="Access to Calis" description="Without it, nothing else this group grants can be used.">
        <Checkbox
          label={SCOPE_MEMBER_LABEL}
          checked={draft.scopeMember}
          onChange={(event) => setDraft(setScopeMember(draft, event.target.checked))}
          disabled={busy}
        />
      </Fieldset>

      <Fieldset legend="Administrative sections" description="Each section reaches every account in Calis. Only a superuser can approve, deny or edit groups, whatever is ticked here.">
        {ADMIN_SECTIONS.map((section) => {
          const grant = sectionOf(section);
          return (
            <div key={section} className="grants-editor__section">
              <span className="grants-editor__section-name">
                {ADMIN_SECTION_LABELS[section]}
                <Chip tone="warning" icon="shield">{sectionReachLabel(section)}</Chip>
                <span className="grants-editor__section-description">{ADMIN_SECTION_DESCRIPTIONS[section]}</span>
              </span>
              <Checkbox
                label="View"
                checked={grant.view}
                onChange={(event) => setDraft(setSectionGrant(draft, section, { view: event.target.checked }))}
                disabled={busy}
              />
              {actOffered(section) ? (
                <Checkbox
                  label="Act"
                  description={grant.act && !grant.view ? 'Act includes view' : undefined}
                  checked={grant.act}
                  onChange={(event) => setDraft(setSectionGrant(draft, section, { act: event.target.checked }))}
                  disabled={busy}
                />
              ) : null}
            </div>
          );
        })}
      </Fieldset>

      <Fieldset legend="Capabilities">
        {GLOBAL_CAPABILITIES.map((name) => {
          const floorMet = floorFor(name);
          return (
            <Checkbox
              key={name}
              label={GLOBAL_CAPABILITY_LABELS[name]}
              description={capabilityDescription(name, floorMet)}
              checked={draft.globals.includes(name)}
              onChange={(event) => setDraft(setGlobalGrant(draft, name, event.target.checked))}
              disabled={busy}
            />
          );
        })}
      </Fieldset>

      {/* Why (03-components §3.3: a form with unsaved changes SHOULD warn before navigation away; Phase 1c finding G9): the
          grants are saved as one set, and leaving the page dropped an unsaved set without a word. Not while the save is in
          flight: the set has left, and leaving loses nothing. */}
      <UnsavedChangesGuard when={dirty && !busy} what="the changes to this group's grants" />
      <div className="grants-editor__actions">
        <Button type="submit" variant="primary" disabled={!dirty} disabledReason={dirty ? undefined : 'No changes to save.'} loading={busy}>Save grants</Button>
        {dirty
          ? <Button variant="ghost" onClick={() => setDraft(saved)} disabled={busy} disabledReason={busy ? 'Wait for the save to finish.' : undefined}>Discard changes</Button>
          : <span className="grants-editor__status">No changes to save</span>}
        {dirty && group.memberCount > 0 ? (
          <span className="grants-editor__status">{`Changes what ${pluralise(group.memberCount, { one: '# member', other: '# members' })} can do.`}</span>
        ) : null}
      </div>
    </form>
  );
}

// --- Effective access preview ------------------------------------------------------------------------

/** Exported for the rendering tests (test/server/client-pages/admin-groups.test.ts); the page composes it. */
export function EffectiveAccess({ group }: { group: AdminGroupDetail }) {
  const access = group.effectiveAccess;
  const yes = <StatusPill tone="success">Yes</StatusPill>;
  const no = <StatusPill tone="neutral" icon="minus">No</StatusPill>;
  return (
    <dl className="effective-access sheet">
      <dt>Access to Calis</dt>
      <dd>{access.scopeAccess ? yes : no}</dd>
      {access.sections.map((section) => (
        <div key={section.section} className="effective-access__row">
          <dt>{ADMIN_SECTION_LABELS[section.section]}</dt>
          <dd>
            {/* `act` is the server's word (routes/admin/groups.ts effectiveAccessPreview): true only where a delegable route honours it, so "View and act" is never shown for a grant that opens nothing (05-administration §4). */}
            {section.view ? <StatusPill tone="success">{section.act ? 'View and act' : 'View'}</StatusPill> : no}
            {section.view ? <span className="text-xs muted">{sectionReachLabel(section.section)}</span> : null}
          </dd>
        </div>
      ))}
      {access.globals.map((global) => (
        <div key={global.name} className="effective-access__row">
          <dt>{GLOBAL_CAPABILITY_LABELS[global.name]}</dt>
          <dd>
            {global.effective ? yes : global.granted && !global.floorMet ? <StatusPill tone="warning">Granted, not configured</StatusPill> : global.granted ? <StatusPill tone="warning">Granted, no base access</StatusPill> : no}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// --- Members editor ----------------------------------------------------------------------------------

export function MembersEditor({ group, busy, onChange }: { group: AdminGroupDetail; busy: boolean; onChange: (subjectIds: string[], message: string, previous: string[]) => Promise<boolean> }) {
  const users = useRequest<AdminUsersResponse>((signal) => adminApi.users(signal), []);
  const [chosen, setChosen] = useState('');
  const memberIds = useMemo(() => group.members.map((member) => member.id), [group.members]);
  // Why: only approved accounts may hold a membership (the store refuses others), so the picker
  // offers only those, minus the people already in.
  const candidates: AdminUserRow[] = useMemo(
    () => (users.data?.roster ?? []).filter((row) => row.status === 'approved' && !memberIds.includes(row.id)),
    [users.data, memberIds],
  );

  // Why the editor knows which of its own writes is in flight: `busy` is the page's one flag for every write, and the
  // design system's loading state belongs to the control whose write it is (03-components §2) — the others are inert
  // for that reason and say so (05-client §5.5; conformity D3, the owner's brief of 2026-10-05).
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const waiting = busy ? 'Wait for the current change to finish.' : undefined;

  const add = async (): Promise<void> => {
    const candidate = candidates.find((row) => row.id === chosen);
    if (!candidate) return;
    setAdding(true);
    try {
      const ok = await onChange([...memberIds, candidate.id], `Added ${candidate.displayName} to ${group.name}.`, memberIds);
      if (ok) setChosen('');
    } finally {
      setAdding(false);
    }
  };

  const remove = async (member: AdminGroupDetail['members'][number]): Promise<void> => {
    setRemoving(member.id);
    try {
      await onChange(memberIds.filter((value) => value !== member.id), `Removed ${member.displayName} from ${group.name}.`, memberIds);
    } finally {
      setRemoving(null);
    }
  };

  const addReason = users.status === 'loading'
    ? 'The accounts are still loading.'
    : candidates.length === 0
      ? 'Every approved account is already in this group.'
      : chosen === ''
        ? 'Choose an account first.'
        : waiting;

  const columns: DataTableColumn<AdminGroupDetail['members'][number]>[] = [
    { key: 'name', header: 'Identity', card: 'title', sortValue: (row) => `${row.displayName} ${row.email}`, cell: (row) => <IdentityCell row={row} to={PATHS.adminUser(row.id)} /> },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      name: 'Actions',
      sortable: false,
      align: 'end',
      card: 'actions',
      cell: (row) => <Button size="sm" variant="danger-outline" onClick={() => void remove(row)} disabled={busy} disabledReason={waiting} loading={removing === row.id}>Remove</Button>,
    },
  ];

  return (
    <div className="stack">
      <div className="member-picker">
        {users.status === 'error' && users.error ? (
          <FailureView failure={users.error} what="the accounts to choose from" onRetry={users.reload} compact />
        ) : (
          <>
            <SelectField
              className="member-picker__field"
              // A picker's label is a noun (05-content §3, as shipped 2026-10-05); "Add to group" beside it is the verb.
              label="Member to add"
              hint={users.status === 'loading' ? 'Loading accounts…' : candidates.length === 0 ? 'Every approved account is already in this group.' : undefined}
              value={chosen}
              onChange={(event) => setChosen(event.target.value)}
              options={candidates.map((row) => ({ value: row.id, label: `${row.displayName} — ${row.email}` }))}
              placeholderOption={candidates.length === 0 ? 'Nobody to add' : 'Choose an account'}
              disabled={busy || users.status === 'loading' || candidates.length === 0}
            />
            <Button variant="secondary" icon="plus" onClick={() => void add()} disabled={busy || chosen === ''} disabledReason={addReason} loading={adding}>Add to group</Button>
          </>
        )}
      </div>
      <DataTable<AdminGroupDetail['members'][number], string>
        caption={`Members of ${group.name}`}
        failureNoun="the group’s members"
        columns={columns}
        rows={group.members}
        rowKey={(row) => row.id}
        sort={{ defaultSort: { key: 'name', dir: 'asc' }, namespace: 'members' }}
        clientSort
        empty={{ title: 'No members yet', description: 'Add an approved account above.' }}
      />
    </div>
  );
}

// --- Grant trail -------------------------------------------------------------------------------------

const TRAIL_ACTION_LABELS: Readonly<Record<string, string>> = {
  'group.create': 'Created the group',
  'group.update': 'Changed details',
  'group.delete': 'Deleted the group',
  'scope.grant': 'Granted access to Calis',
  'scope.revoke': 'Revoked access to Calis',
  'section.set': 'Set a section grant',
  'section.revoke': 'Revoked a section grant',
  'global.grant': 'Granted a capability',
  'global.revoke': 'Revoked a capability',
  'member.add': 'Added a member',
  'member.remove': 'Removed a member',
};

/** Why a membership changed, in plain words; this page's own editor (`group_members_update`) needs none. */
const REASON_LABELS: Readonly<Record<string, string>> = {
  subject_groups_update: "from the account's page",
  approve: 'on approval',
  reinstate: 'on reinstatement',
  deny: 'on denial',
  suspend: 'on suspension',
  subject_deleted: 'on account deletion',
  group_deleted: 'on group deletion',
};

/** A section grant's values in the editor's words, or null where the value is not a grant. */
function grantWord(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const grant = value as { view?: boolean; act?: boolean };
  return grant.act ? 'view and act' : grant.view ? 'view' : 'off';
}

function trailDetail(entry: GroupAuditEntry): string {
  const detail = entry.detail;
  if (detail === null || typeof detail !== 'object') return '—';
  const record = detail as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof record.subjectName === 'string') parts.push(record.subjectName);
  if (typeof record.section === 'string') {
    parts.push(ADMIN_SECTION_LABELS[record.section as AdminSection] ?? record.section);
    // Why: the store writes a section grant as { section, before, after } (stores/groups.ts), so the
    // row shows the change as before → after; an unset before reads —, and a revoke ends at off.
    const afterWord = grantWord(record.after);
    const beforeWord = grantWord(record.before);
    if (afterWord) parts.push(`${beforeWord ?? '—'} → ${afterWord}`);
    else if (beforeWord && entry.action === 'section.revoke') parts.push(`${beforeWord} → off`);
  }
  if (typeof record.name === 'string' && typeof record.subjectName !== 'string') parts.push(GLOBAL_CAPABILITY_LABELS[record.name as GlobalCapability] ?? record.name);
  if (typeof record.capability === 'string') parts.push(record.capability === 'member' ? SCOPE_MEMBER_LABEL : (GLOBAL_CAPABILITY_LABELS[record.capability as GlobalCapability] ?? record.capability));
  const reason = typeof record.reason === 'string' ? REASON_LABELS[record.reason] : undefined;
  if (reason) parts.push(reason);
  if (parts.length === 0) {
    const keys = Object.keys(record).filter((key) => key !== 'subjectId');
    return keys.length > 0 ? keys.map((key) => `${key}: ${String(record[key])}`).join(', ') : '—';
  }
  return parts.join(' · ');
}

/** Exported for the rendering tests (test/server/client-pages/group-history-order.test.ts); the page composes it. */
export function GroupTrail({ trail }: { trail: RequestState<GroupAuditResponse> }) {
  const columns: DataTableColumn<GroupAuditEntry>[] = [
    { key: 'when', header: 'When', type: 'date', card: 'meta', sortValue: (row) => row.createdAt, cell: (row) => <RelativeTime value={row.createdAt} mode="both" /> },
    { key: 'action', header: 'Change', card: 'title', sortValue: (row) => TRAIL_ACTION_LABELS[row.action] ?? row.action, cell: (row) => TRAIL_ACTION_LABELS[row.action] ?? operationLabel(row.action) },
    { key: 'detail', header: 'Detail', sortable: false, cell: (row) => trailDetail(row) },
    { key: 'actor', header: 'By', sortValue: (row) => row.actor.name, cell: (row) => (row.actor.id ? <Link to={PATHS.adminUser(row.actor.id)} className="hit-area">{row.actor.name}</Link> : row.actor.name) },
  ];
  const status: DataTableStatus = trail.status === 'loading'
    ? { kind: 'loading', rowCount: 3 }
    : trail.status === 'error' && trail.error
      ? { kind: 'error', failure: trail.error, onRetry: trail.reload }
      : { kind: 'ready' };
  return (
    <DataTable<GroupAuditEntry, string>
      caption="Grant and membership history for this group, newest first"
      failureNoun="this group’s history"
      columns={columns}
      rows={trail.data?.items ?? []}
      rowKey={(row) => row.id}
      status={status}
      // Why newest first: a carrier's own change history takes the audit log's default order (06-modules §2.2, as design
      // system 8.5.0's A19 restates it), so the app carries one convention for one kind of list (Phase 1c finding R13).
      sort={{ defaultSort: { key: 'when', dir: 'desc' }, namespace: 'history' }}
      clientSort
      empty={{ title: 'No changes recorded', description: 'A group\'s creation is itself recorded, so an empty history usually means it could not be read.', action: <Button variant="secondary" icon="refresh" onClick={trail.reload}>Try again</Button> }}
    />
  );
}

// --- Edit details ------------------------------------------------------------------------------------

function EditDetailsDialog({ group, open, busy, onClose, onSave }: { group: AdminGroupDetail; open: boolean; busy: boolean; onClose: () => void; onSave: (body: { name?: string; description?: string | null; policyNote?: string | null }) => Promise<boolean> }) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? '');
  const [policyNote, setPolicyNote] = useState(group.policyNote ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setName(group.name);
      setDescription(group.description ?? '');
      setPolicyNote(group.policyNote ?? '');
      setNameError(null);
    }
  }, [open, group]);
  const dirty = name !== group.name || description !== (group.description ?? '') || policyNote !== (group.policyNote ?? '');

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (name.trim() === '') {
      setNameError('The group needs a name.');
      return;
    }
    const body: { name?: string; description?: string | null; policyNote?: string | null } = {};
    if (name.trim() !== group.name) body.name = name.trim();
    if (description.trim() !== (group.description ?? '')) body.description = description.trim() === '' ? null : description.trim();
    if (policyNote.trim() !== (group.policyNote ?? '')) body.policyNote = policyNote.trim() === '' ? null : policyNote.trim();
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    await onSave(body);
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title="Edit group details"
      initialFocus="first"
      unsavedChanges={dirty && !busy}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy} disabledReason={busy ? 'Wait for the save to finish.' : undefined}>Cancel</Button>
          <Button variant="primary" type="submit" form="edit-group-form" loading={busy} disabled={!dirty} disabledReason={dirty ? undefined : 'No changes to save.'}>Save details</Button>
        </>
      }
    >
      <form id="edit-group-form" className="stack" onSubmit={(event) => void submit(event)} noValidate>
        {nameError ? <FormErrorSummary errors={[nameError]} /> : null}
        <TextField label="Name" required value={name} onChange={(event) => setName(event.target.value)} error={nameError} autoComplete="off" maxLength={120} hint="Unique; shown on every member's row." />
        <TextareaField label="Description" value={description} onChange={(event) => setDescription(event.target.value)} rows={2} maxLength={600} hint="Optional. Who this group is for." />
        <TextareaField label="Policy note" value={policyNote} onChange={(event) => setPolicyNote(event.target.value)} rows={2} maxLength={600} hint="Optional. Guidance for administrators; it enforces nothing." />
      </form>
    </Dialog>
  );
}

// --- Delete with blast radius ------------------------------------------------------------------------

function DeleteGroupDialog({ group, open, onCancel, onDeleted, onDenied, onChanged }: { group: AdminGroupDetail; open: boolean; onCancel: () => void; onDeleted: (name: string) => void; onDenied: (failure: ApiFailure) => void; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // What members lose is worded with the server's act, as the preview is: a stored act no route honours is not lost, since it was never usable.
  const radius = describeBlastRadius(group.blastRadius, actDelegableSectionsOf(group.effectiveAccess));

  const confirm = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    // Why: the count shown is sent back; the server refuses (409) if membership changed since,
    // so the operator never deletes access from people they did not see listed.
    const result = await adminApi.deleteGroup(group.id, group.blastRadius.memberCount);
    setBusy(false);
    if (!result.ok) {
      if (failureKind(result) === 'denied') {
        onDenied(result);
        return;
      }
      if (result.code === 'conflict') {
        setError('The group\'s membership changed since this was shown, so it was not deleted. The page has been refreshed — review the list and try again.');
        onChanged();
        return;
      }
      setError(inlineFailureText(result));
      return;
    }
    onDeleted(group.name);
  };

  return (
    <ConfirmDialog
      open={open}
      title={`Delete ${group.name}?`}
      confirmLabel="Delete group"
      danger
      busy={busy}
      typeToConfirm={group.name}
      onCancel={() => { if (!busy) { setError(null); onCancel(); } }}
      onConfirm={confirm}
      error={error}
    >
      <div className="stack stack--2">
        <p><strong>{radius.summary}</strong></p>
        {radius.loses.length > 0 ? (
          <ul className="blast-radius__list">
            {radius.loses.map((phrase) => <li key={phrase}>{phrase}</li>)}
          </ul>
        ) : null}
        {radius.allAccess ? <p><strong>{radius.allAccess}</strong></p> : null}
        {radius.names.length > 0 ? (
          <p className="blast-radius__names">
            {formatListWithRest(radius.names, radius.more, { one: '# more member', other: '# more members' })}
          </p>
        ) : null}
        <p>The group's change history is kept. This cannot be undone.</p>
      </div>
    </ConfirmDialog>
  );
}
